import type { ChatMessage } from '../../types'

export function hasAwaitingApproval(messages: readonly ChatMessage[]) {
  return messages.some(message => message.approval?.state === 'awaiting')
}

export function withCurrentApproval(messages: ChatMessage[]): ChatMessage[] {
  const newestAwaiting = messages.findLast(message => message.approval?.state === 'awaiting')
  if (!newestAwaiting) {
    return messages
  }
  return messages.map(message => {
    if (message.id === newestAwaiting.id || message.approval?.state !== 'awaiting') {
      return message
    }
    return {
      ...message,
      approval: { ...message.approval, state: 'rejected', rejection: 'superseded' },
    }
  })
}

export type ApprovalDecision =
  | 'approved'
  | 'revised'
  | 'insufficient_coins'
  | 'unavailable'
  | 'subscription'
  | 'rejected'

const DECISION_LABEL: Record<ApprovalDecision, string> = {
  approved: 'Approved',
  revised: 'Revised',
  insufficient_coins: 'Rejected - Insufficient coins',
  unavailable: 'Rejected - Request unavailable',
  subscription: 'idea.ai subscription required',
  rejected: 'Rejected from user',
}

export function approvalDecision(
  approval: NonNullable<ChatMessage['approval']>,
): ApprovalDecision | undefined {
  if (approval.state === 'approved') {
    return 'approved'
  }
  if (approval.state !== 'rejected') {
    return undefined
  }
  if (approval.rejection === 'insufficient_balance' || approval.rejection === 'payment_required') {
    return 'insufficient_coins'
  }
  if (approval.rejection === 'request_unavailable') {
    return 'unavailable'
  }
  if (approval.rejection === 'superseded') {
    return 'revised'
  }
  if (approval.rejection === 'subscription_required') {
    return 'subscription'
  }
  return 'rejected'
}

export function approvalStatus(approval: NonNullable<ChatMessage['approval']>): string | undefined {
  const decision = approvalDecision(approval)
  if (!decision) {
    return undefined
  }
  return DECISION_LABEL[decision]
}
